import React, { useState, useEffect } from "react";
import { 
  Calculator, Target, GraduationCap, Users, Globe, Clock, AlertCircle, 
  BookOpen, TrendingUp, ChevronRight 
} from "lucide-react";
import { motion } from "framer-motion";
import ContactForm from "../components/ContactForm";
import { Link } from "react-router-dom";

const TOEFLPage = () => {
  const [readingScore, setReadingScore] = useState(25);
  const [listeningScore, setListeningScore] = useState(25);
  const [speakingScore, setSpeakingScore] = useState(25);
  const [writingScore, setWritingScore] = useState(25);
  const [totalScore, setTotalScore] = useState(100);
  const [overallBand, setOverallBand] = useState("B2");

  const updateScore = () => {
    const total = readingScore + listeningScore + speakingScore + writingScore;
    setTotalScore(total);
    
    const bands = {
      0: "Below A1", 42: "A2", 71: "B1", 94: "B2", 
      109: "B2+/C1", 119: "C1", 130: "C1+/C2"
    };
    const band = Object.keys(bands).reverse().find(key => total >= parseInt(key)) || "Below A1";
    setOverallBand(bands[band] || band);
  };

  useEffect(() => {
    updateScore();
  }, [readingScore, listeningScore, speakingScore, writingScore]);

  const selfSelectCards = [
    { icon: GraduationCap, title: "Graduate Program Applicants", desc: "One of the most widely accepted tests for 12,000+ universities worldwide" },
    { icon: Users, title: "Visa & Scholarship Applicants", desc: "Accepted for UK/Australia visas where TOEFL is approved. Check visa subclass requirements" },
    { icon: TrendingUp, title: "STEM/Research Programs", desc: "Historically preferred by technical and research-heavy graduate programs" }
  ];

  const benchmarks = [
    { score: "60-78", meaning: "B1 level", unis: "Pathway programs, foundation courses" },
    { score: "79-100", meaning: "B2 level", unis: "Most undergraduate admissions" },
    { score: "101-114", meaning: "B2+/C1", unis: "Competitive graduate programs" },
    { score: "115+", meaning: "C1+/C2", unis: "Highly selective research programs" }
  ];

  const myths = [
    { text: "TOEFL is harder than IELTS", counter: "Different formats test same CEFR levels. Choose based on skill strengths" },
    { text: "Home Edition not accepted everywhere", counter: "Most universities accept both formats. Always verify institutional policy" },
    { text: "Speaking scores can't improve", counter: "Targeted practice improves pronunciation, fluency, organization" },
    { text: "Only Reading matters for academics", counter: "Balanced scoring across all sections. Integrated tasks test real academic English" }
  ];

  const testFormat = [
    { section: "Reading", duration: "~54-72 min" },
    { section: "Listening", duration: "~41-57 min" },
    { section: "Speaking", duration: "17 min (4 tasks)" },
    { section: "Writing", duration: "50 min (2 tasks)" }
  ];

  const preparationFlow = [
    { step: "Diagnostic Assessment", desc: "Full TOEFL iBT with section analysis and ETS score preview format" },
    { step: "Integrated Skills Practice", desc: "Read-listen-write and read-listen-speak task combinations" },
    { step: "Speaking Fluency Training", desc: "17-45s response organization, pronunciation, academic vocabulary" },
    { step: "Proctored Simulations", desc: "Full 3-hour TOEFL iBT with official 0-120 scoring" }
  ];

  const getPercentileBand = (score) => {
    if (score >= 115) return "Top ~10%";
    if (score >= 101) return "Top ~25%";
    if (score >= 90) return "Top ~40%";
    if (score >= 80) return "Average";
    return "Below average";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      {/* 1. Hero + Calculator */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-light text-slate-900 leading-tight">
              TOEFL iBT Overview
            </h1>
            <p className="text-xl text-slate-600 max-w-lg leading-relaxed">
              One of the most widely accepted English proficiency tests for 12,000+ universities in 160+ countries.
            </p>
            <p className="text-lg text-slate-500">
              TOEFL preferred for graduate/research programs. IELTS/PTE often more convenient for visas. 0-120 total score, results in 4-8 days.
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-slate-100 max-h-[32rem] overflow-y-auto"
          >
            <div className="flex items-center gap-3 mb-4">
              <Calculator className="w-6 h-6 text-emerald-600" />
              <h3 className="text-xl font-semibold text-slate-900">Estimate Your TOEFL Score</h3>
            </div>
            <p className="text-xs text-slate-500 mb-6 italic">
              Four equally weighted sections (0-30 each). Approximate percentile (indicative only).
            </p>
            <div className="space-y-3">
              {[
                { label: "Reading (0-30)", value: readingScore, setter: setReadingScore },
                { label: "Listening (0-30)", value: listeningScore, setter: setListeningScore },
                { label: "Speaking (0-30)", value: speakingScore, setter: setSpeakingScore },
                { label: "Writing (0-30)", value: writingScore, setter: setWritingScore }
              ].map(({ label, value, setter }, i) => (
                <div key={label} className="space-y-1">
                  <label className="text-xs font-medium text-slate-700">{label}</label>
                  <input
                    type="range"
                    min="0" max="30"
                    value={value}
                    onChange={e => setter(+e.target.value)}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500 hover:accent-emerald-600"
                  />
                  <span className="text-xs font-mono text-slate-600">{value}</span>
                </div>
              ))}
              <div className="pt-6 border-t border-slate-200">
                <div className="text-3xl font-bold text-slate-900 mb-1">Total: {totalScore}</div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-emerald-700">{getPercentileBand(totalScore)}</span>
                  <span className="text-xs text-slate-500">({overallBand} CEFR, approx.)</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Who Takes TOEFL */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Who Takes TOEFL?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Preferred by graduate programs and research universities. Strong global acceptance for academic admissions.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {selfSelectCards.map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="group bg-white rounded-3xl p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 border border-slate-100 hover:border-emerald-200"
            >
              <card.icon className="w-12 h-12 text-emerald-600 mb-6 mx-auto" />
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">{card.title}</h3>
              <p className="text-slate-600 leading-relaxed">{card.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3. CTA Cluster */}
      <section className="py-24 px-6 md:px-12 bg-emerald-50 border-t border-emerald-200">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-2xl shadow-lg">
            <BookOpen className="w-8 h-8 text-emerald-600" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900">Next Steps</h3>
              <p className="text-sm text-slate-600">Review TOEFL preparation for your target universities</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/test-prep#toefl">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-emerald-600 hover:bg-emerald-700 px-8 py-4 rounded-2xl text-lg font-semibold shadow-xl flex items-center gap-2"
            >
              TOEFL Preparation Programs
              <ChevronRight className="w-5 h-5" />
            </motion.button>
            </Link>
            <Link to="/test-prep">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="border-2 border-slate-600 hover:border-slate-400 bg-white text-slate-900 px-8 py-4 rounded-2xl text-lg font-semibold flex items-center gap-2"
            >
              All Test Prep
            </motion.button>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Score Benchmarks */}
      <section className="py-24 px-6 md:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            What Your Score Means
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
            Admissions holistic: GPA primary. TOEFL confirms academic readiness. Score consideration policies vary by university.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {benchmarks.map((bench, i) => (
            <motion.div
              key={bench.score}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-8 hover:shadow-xl border border-slate-100"
            >
              <div className="text-3xl font-bold text-emerald-600 mb-4">{bench.score}</div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3">{bench.meaning}</h4>
              <p className="text-slate-600">{bench.unis}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 5. Global Acceptance */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Global Acceptance
          </h2>
          <p className="text-xl text-slate-600">
            12,000+ universities across 160 countries accept TOEFL iBT.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-3xl p-10">
            <Globe className="w-16 h-16 text-emerald-600 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">University Admissions</h3>
            <p className="text-lg text-slate-600">Both test center and Home Edition accepted by most universities. Verify individual institutional policies.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-10">
            <Target className="w-16 h-16 text-blue-600 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">Visa Requirements</h3>
            <p className="text-lg text-slate-600">Accepted where TOEFL listed as approved test for UK/Australia student visas. Requirements vary by subclass.</p>
          </motion.div>
        </div>
      </section>

      {/* 6. Common Misconceptions */}
      <section className="py-24 px-6 md:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-12 text-center">
            Common Misconceptions
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {myths.map((myth, i) => (
              <motion.div
                key={myth.text}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="flex flex-col gap-4 p-6 bg-white rounded-2xl border border-slate-200 hover:border-amber-200 group"
              >
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-8 h-8 text-amber-500 flex-shrink-0 mt-1" />
                  <p className="text-slate-700 group-hover:text-slate-900 font-medium">{myth.text}</p>
                </div>
                <p className="text-sm text-slate-600 font-semibold bg-amber-50 p-3 rounded-xl">
                  {myth.counter}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Test Structure */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Test Structure
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-16">
            Total: ~3 hours. Internet-delivered. All four skills equally weighted.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {testFormat.map((format, i) => (
            <motion.div
              key={format.section}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 text-center border border-slate-100 hover:shadow-lg"
            >
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{format.section}</h3>
              <Clock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <div className="text-2xl font-bold text-slate-900">{format.duration}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 8. How We Help You Prepare */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            How We Help You Prepare
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Targeted TOEFL iBT preparation with integrated skills practice and official ETS format alignment.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {preparationFlow.map((step, i) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, x: i % 2 ? 20 : -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-10 border border-slate-100 hover:shadow-xl hover:border-emerald-200"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-emerald-700">{i + 1}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{step.step}</h3>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 9. ContactForm */}
      <section id="contact-form" className="max-w-6xl mx-auto px-8 pb-12 bg-slate-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Questions About TOEFL?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Schedule consultation for university-specific TOEFL requirements and preparation strategy.
          </p>
        </div>
        <ContactForm />
      </section>
    </div>
  );
};

export default TOEFLPage;
